import { ArrowUpDown, Lock, Minus, TrendingDown, TrendingUp } from "lucide-react";
import type { DemandLevel } from "../../types/api";
import type { PricingAction } from "../../services/outlookApi";
import { DEMAND_TINT_CLASS } from "../calendarMetricScale";
import { cn } from "../ui/utils";
import { listed, type OutlookTone } from "./outlookCopy";

/**
 * The marks the outlook leads its words with.
 *
 * A status indicator is "small colored dots … using the status tokens, not
 * large icons", and the word beside it is always the message — the mark only
 * lets the eye find the line that needs reading. Each sits in the same
 * one-rem box, so the words of every column start at the same place whichever
 * mark leads them. All are hidden from assistive technology: the sentence says
 * everything the mark does.
 */

const MARK_BOX = "flex size-4 shrink-0 items-center justify-center";

const TONE_CLASS: Record<OutlookTone, string> = {
    good: "bg-success",
    neutral: "bg-muted-foreground/50",
    caution: "bg-warning",
    bad: "bg-destructive",
    // Hollow: no reading is not a neutral reading.
    unknown: "border border-muted-foreground/60",
};

interface OutlookMarkProps {
    tone: OutlookTone;
    className?: string;
}

/** A dot in the tone of the mood beside it. */
export function OutlookMark({ tone, className }: OutlookMarkProps) {
    return (
        <span aria-hidden="true" className={cn(MARK_BOX, className)}>
            <span data-tone={tone} className={cn("size-2 rounded-full", TONE_CLASS[tone])} />
        </span>
    );
}

interface DemandMarkProps {
    level: DemandLevel | null;
    className?: string;
}

/**
 * How a night sells, in the ramp the Prices calendar tints the same night
 * with — one key for both screens. A night nobody could grade is hollow, and
 * so is one graded at a level this page does not list: read straight out of
 * the ramp, it was a mark with no colour at all.
 */
export function DemandMark({ level, className }: DemandMarkProps) {
    const tint = listed(DEMAND_TINT_CLASS, level);
    return (
        <span aria-hidden="true" className={cn(MARK_BOX, className)}>
            <span
                data-demand={tint === null ? "unknown" : level}
                className={cn("size-2.5 rounded-[2px]", tint ?? "border border-muted-foreground/60")}
            />
        </span>
    );
}

interface PricingMarkProps {
    action: PricingAction | null;
    className?: string;
}

/**
 * Which way Optivo moved the price. A direction, not a verdict — a discount on
 * the last free room is the plan working — so it wears the adjustment colours
 * rather than the ones that grade an outcome. No action, and one this page does
 * not list, are both the hollow mark; `outlook-moods.parity.test.tsx` holds
 * every action the server can send to a mark of its own, since the `default`
 * below asks a new one for no case.
 */
export function PricingMark({ action, className }: PricingMarkProps) {
    const box = cn(MARK_BOX, className);
    switch (action) {
        case "discounting":
            return <span aria-hidden="true" className={box}><TrendingDown className="size-4 text-adjustment-down" /></span>;
        case "raising":
            return <span aria-hidden="true" className={box}><TrendingUp className="size-4 text-adjustment-up" /></span>;
        case "holding":
            return <span aria-hidden="true" className={box}><Minus className="size-4 text-muted-foreground" /></span>;
        case "adjusting":
            return <span aria-hidden="true" className={box}><ArrowUpDown className="size-4 text-muted-foreground" /></span>;
        case "locked":
            return <span aria-hidden="true" className={box}><Lock className="size-3.5 text-locked" /></span>;
        case "nothingToSell":
            return <OutlookMark tone="neutral" className={className} />;
        default:
            return <OutlookMark tone="unknown" className={className} />;
    }
}
